/**
 * Task Management Connector configuration component.
 * Specialized configuration for Task Management connector (JIRA / Azure DevOps).
 */

// React Imports
import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  TextField,
} from '@mui/material';

export interface TaskManagementConnectorConfigProps {
  settingsJson?: string;
  onChange?: (settingsJson: string) => void;
}

/**
 * Task Management Connector configuration component.
 */
export const TaskManagementConnectorConfig = ({
  settingsJson = '{}',
  onChange,
}: TaskManagementConnectorConfigProps) => {
  const [provider, setProvider] = useState<'jira' | 'azuredevops'>('jira');
  const [defaultProject, setDefaultProject] = useState('');
  const [defaultIssueType, setDefaultIssueType] = useState('Task');

  const handleProviderChange = (newProvider: 'jira' | 'azuredevops') => {
    setProvider(newProvider);
    const settings = JSON.parse(settingsJson || '{}');
    settings.provider = newProvider;
    onChange?.(JSON.stringify(settings));
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Task Management Provider Configuration
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select Provider
          </Typography>
          <ToggleButtonGroup
            value={provider}
            exclusive
            onChange={(_, newProvider) => newProvider && handleProviderChange(newProvider)}
            fullWidth
          >
            <ToggleButton value="jira">JIRA</ToggleButton>
            <ToggleButton value="azuredevops">Azure DevOps</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {provider === 'jira' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Default Project Key"
              value={defaultProject}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDefaultProject(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultProject = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Default JIRA project key (e.g., PROJ)"
            />
            <TextField
              label="Default Issue Type"
              value={defaultIssueType}
              onChange={(e) => {
                setDefaultIssueType(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultIssueType = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Default issue type (e.g., Task, Bug, Story)"
            />
            <Alert severity="info">
              JIRA supports both Cloud (OAuth) and Server (API token) authentication. Configure authentication in the Authentication step.
            </Alert>
          </Box>
        )}

        {provider === 'azuredevops' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Default Project Name"
              value={defaultProject}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDefaultProject(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultProject = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Default Azure DevOps project name"
            />
            <TextField
              label="Default Work Item Type"
              value={defaultIssueType}
              onChange={(e) => {
                setDefaultIssueType(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultWorkItemType = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
              fullWidth
              helperText="Default work item type (e.g., Task, Bug, User Story)"
            />
            <Alert severity="info">
              Azure DevOps supports Personal Access Token (PAT) or OAuth authentication. Configure in the Authentication step.
            </Alert>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
